import { Project, Target } from '@griply/assets';
import { EntityLinks, GoalType } from '@griply/types';
import { getBarPosition, isGoalArchived } from '@griply/utils';
import { format } from 'date-fns';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { GoalContextMenu } from 'shared/components/connected/goal-context-menu';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { DateRange } from 'shared/types/date-range';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Timestamp } from 'shared/types/timestamp';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';
import { getGoalDetailUrl } from 'shared/utils/get-goal-detail-url';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import * as Styled from './goal-bar.style';
import { MovementModes, useDragHandlers } from './use-drag-handlers';

export type GoalBarProps = {
  goal: Goal;
  goals: Goal[];
  // Caller-supplied: a UI component must not fetch — without it a migrated goal
  // resolves no parent and never inherits its parent's archived state.
  links?: EntityLinks;
  lifeAreas: LifeArea[];
  dateRange: DateRange;
  dayWidth: number;
  onUpdateDates: (
    goalId: ID,
    newStartDate: Timestamp,
    newEndDate: Timestamp,
  ) => void;
};

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

export const GoalBar: React.FC<GoalBarProps> = React.memo(
  ({ goal, goals, links, lifeAreas, dateRange, dayWidth, onUpdateDates }) => {
    const itemContainerRef = useRef<HTMLDivElement>(null);
    const resizeStartHandle = useRef<HTMLDivElement>(null);
    const resizeEndHandle = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();
    const {
      open: openMenu,
      close: closeMenu,
      position: menuPosition,
    } = useContextMenuState({
      disabled: !!goal.frozenAt,
    });

    const [optimisticStartDate, setOptimisticStartDate] = useState<
      Timestamp | undefined
    >();
    const [optimisticEndDate, setOptimisticEndDate] = useState<
      Timestamp | undefined
    >();

    const wasDragged = useRef(false);

    const onNavigate = useCallback(() => {
      if (wasDragged.current) {
        wasDragged.current = false;
        return;
      }
      navigate(getGoalDetailUrl(goal));
    }, [navigate, goal]);

    const handleDragMove = useCallback(() => {
      wasDragged.current = true;
    }, []);

    const { handleMouseDown } = useDragHandlers({
      goal,
      onDragEnd: (id, newStart, newEnd) => {
        setOptimisticStartDate(newStart);
        setOptimisticEndDate(newEnd);
        onUpdateDates(id, newStart, newEnd);

        // Reset wasDragged after drag ends
        setTimeout(() => {
          wasDragged.current = false;
        }, 0);
      },
      dayWidth,
      itemContainerRef,
      onPreviewDatesChange: (start, end) => {
        setPreviewStartDate(start);
        setPreviewEndDate(end);
      },
      onMouseMove: handleDragMove,
    });
    const [previewStartDate, setPreviewStartDate] = useState<
      Date | undefined
    >();
    const [previewEndDate, setPreviewEndDate] = useState<Date | undefined>();

    const goalColor = useMemo(
      () => getLinkedItemColor(goal.id, { lifeAreas, goals }),
      [goal.id, goals, lifeAreas],
    );

    const isArchived = useMemo(
      () => isGoalArchived(goal, goals, { links }),
      [goal, goals, links],
    );

    const taskCounts = useTaskCounts();
    const isProject = goal.type === GoalType.Project;

    const completionPercentage = useMemo(
      () =>
        goal
          ? getCompletionPercentage(
              getGoalCompletion(goal, {
                goals,
                goalProgressCounts: taskCounts?.byGoalProgress,
              }),
            )
          : undefined,
      [goal, goals, taskCounts?.byGoalProgress],
    );

    const position = useMemo(
      () =>
        getBarPosition({
          startDate: optimisticStartDate ?? goal.startDate,
          deadline: optimisticEndDate ?? goal.deadline,
          dateRange,
          dayWidth,
        }),
      [
        optimisticStartDate,
        optimisticEndDate,
        goal.startDate,
        goal.deadline,
        dateRange,
        dayWidth,
      ],
    );

    useEffect(() => {
      setOptimisticStartDate(undefined);
    }, [goal.startDate]);

    useEffect(() => {
      setOptimisticEndDate(undefined);
    }, [goal.deadline]);

    if (!position) {
      return null;
    }

    const { left, width } = position;

    const onMove = (e: React.MouseEvent) =>
      handleMouseDown(e, MovementModes.Move);
    const onResizeStart = (e: React.MouseEvent) =>
      handleMouseDown(e, MovementModes.ResizeStart);
    const onResizeEnd = (e: React.MouseEvent) =>
      handleMouseDown(e, MovementModes.ResizeEnd);

    return (
      <>
        <Styled.Bar
          ref={itemContainerRef}
          $left={left}
          $width={width}
          $color={goalColor}
          $completed={!!goal.completedAt}
          $archived={isArchived}
          onContextMenu={openMenu}
          onMouseDown={onMove}
          onClick={onNavigate}
        >
          <Styled.Content>
            {isProject ? (
              <Project
                width={18}
                strokeWidth={1.5}
                color={goalColor ?? undefined}
              />
            ) : (
              <Target
                width={18}
                strokeWidth={1.5}
                color={goalColor ?? undefined}
              />
            )}
            <Styled.NameContainer>{goal.name}</Styled.NameContainer>
            <Styled.ProgressContainer $color={goalColor}>
              <CircleProgress
                percentage={completionPercentage}
                hidePercentage
                isSmall
              />
            </Styled.ProgressContainer>
          </Styled.Content>

          <Styled.ResizeHandle
            ref={resizeStartHandle}
            $position="start"
            onMouseDown={onResizeStart}
            onClick={stopPropagation}
          >
            <Styled.ResizeIndicator $color={goalColor} />
          </Styled.ResizeHandle>

          <Styled.ResizeHandle
            ref={resizeEndHandle}
            $position="end"
            onMouseDown={onResizeEnd}
            onClick={stopPropagation}
          >
            <Styled.ResizeIndicator $color={goalColor} />
          </Styled.ResizeHandle>

          <Tooltip
            label={format(
              previewStartDate ?? optimisticStartDate ?? goal.startDate!,
              'MMM d',
            )}
            referenceElement={resizeStartHandle}
          />
          <Tooltip
            label={format(
              previewEndDate ?? optimisticEndDate ?? goal.deadline!,
              'MMM d',
            )}
            referenceElement={resizeEndHandle}
          />
        </Styled.Bar>

        {!!menuPosition && (
          <GoalContextMenu
            id={goal.id}
            onClose={closeMenu}
            location={menuPosition}
          />
        )}
      </>
    );
  },
);
